import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ActionColumn from 'screens/common/components/ActionColumn';
import { Badge, Button, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faEye
} from '@fortawesome/free-solid-svg-icons';

const TableResponsiveCard = ({
  page,
  cardTittles,
  setIsModalOpen,
  setSelectedRowData,
  handleAction,
  renderCollapsibleContent,
  prepareRow
}) => {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = index => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getCellValue = (rowData, cardTitle) => {
    if (!rowData || !cardTitle) return '';

    if (rowData[cardTitle] !== undefined) {
      return rowData[cardTitle];
    }

    const keys = Object.keys(rowData);
    const matchingKey = keys.find(
      key => key.toLowerCase() === cardTitle.toLowerCase()
    );

    return matchingKey ? rowData[matchingKey] : undefined;
  };

  const getStatusBadge = status => {
    if (!status) return null;
    const s = status.toLowerCase();
    let variant = 'secondary';
    if (
      s.includes('completed') ||
      s.includes('procured') ||
      s.includes('success')
    )
      variant = 'success';
    if (s.includes('started') || s.includes('pending') || s.includes('partial'))
      variant = 'warning';
    if (s.includes('failed') || s.includes('cancelled') || s.includes('danger'))
      variant = 'danger';
    if (s.includes('info') || s.includes('transfer')) variant = 'info';

    return (
      <Badge
        bg={null}
        className={`rounded-pill px-3 py-1 font-sans-serif fw-semi-bold badge-soft-${variant}`}
      >
        {status}
      </Badge>
    );
  };

  return (
    <div className="card-container d-block d-md-none">
      {page.map((row, i) => {
        const rowData = row.original || row;
        if (prepareRow) prepareRow(row);

        const hasMoreData =
          rowData?.more && Object.keys(rowData?.more).length > 0;
        const isExpanded = !!expandedRows[i];

        // Primary title (usually Date)
        const primaryTitle = getCellValue(rowData, cardTittles[0]);
        // Secondary title (usually ID/Link)
        const secondaryTitle = getCellValue(rowData, cardTittles[1]);
        const status =
          getCellValue(rowData, 'Procurement Status') ||
          getCellValue(rowData, 'Transfer Status') ||
          getCellValue(rowData, 'Status');

        return (
          <div
            key={i}
            className="card mb-3 overflow-hidden border-0 shadow-sm"
            style={{
              borderRadius: '12px',
              background: '#ffffff',
              border: '1px solid rgba(0,0,0,0.05)'
            }}
          >
            <div className="card-body p-3">
              {/* Header Section */}
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="flex-grow-1">
                  <div className="mb-1">
                    <span className="text-500 fw-semi-bold fs--1">
                      {primaryTitle}
                    </span>
                  </div>
                  <h6
                    className="mb-0 fw-bold text-primary cursor-pointer"
                    onClick={() => handleAction('edit', rowData)}
                  >
                    {secondaryTitle}
                  </h6>
                </div>
                <div className="d-flex flex-column align-items-end ms-2">
                  <div className="mb-2" onClick={e => e.stopPropagation()}>
                    <ActionColumn
                      row={{ original: rowData }}
                      handleAction={handleAction}
                      traceability={true}
                    />
                  </div>
                  {status && <div>{getStatusBadge(status)}</div>}
                </div>
              </div>

              {/* Grid Content */}
              <div className="row g-2 mb-3">
                {cardTittles.slice(2).map((title, idx) => {
                  if (
                    title.toLowerCase() === 'action' ||
                    title.toLowerCase() === 's no' ||
                    title.toLowerCase().includes('status')
                  )
                    return null;

                  const val = getCellValue(rowData, title);
                  if (val === undefined || val === null || val === '')
                    return null;

                  return (
                    <div key={idx} className="col-6">
                      <div className="p-2 rounded bg-white border-dashed border-1">
                        <small className="text-500 d-block text-uppercase ls-1 fw-bold fs--2 mb-1">
                          {title}
                        </small>
                        <span className="text-800 fw-medium fs--1">
                          {typeof val === 'object' ? val.label || '-' : val}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer Actions */}
              <div className="d-flex justify-content-end align-items-center pt-2 border-top">
                {hasMoreData && (
                  <Button
                    variant="soft-primary"
                    size="sm"
                    className="rounded-pill px-3 py-1 d-flex align-items-center gap-2"
                    onClick={() => toggleRow(i)}
                    aria-expanded={isExpanded}
                  >
                    <span className="fw-semi-bold fs--1">
                      {isExpanded ? 'Hide Details' : 'View Details'}
                    </span>
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronUp : faChevronDown}
                      className="fs--2 transition-base"
                    />
                  </Button>
                )}
              </div>
            </div>

            {/* Expandable Details Area */}
            <Collapse in={isExpanded}>
              <div className="bg-light border-top">
                <div className="p-3 fs--1">
                  {renderCollapsibleContent &&
                    renderCollapsibleContent(rowData.more)}
                </div>
              </div>
            </Collapse>
          </div>
        );
      })}
    </div>
  );
};

TableResponsiveCard.propTypes = {
  page: PropTypes.array.isRequired,
  cardTittles: PropTypes.arrayOf(PropTypes.string),
  setIsModalOpen: PropTypes.func.isRequired,
  setSelectedRowData: PropTypes.func,
  handleAction: PropTypes.func.isRequired,
  prepareRow: PropTypes.func,
  renderCollapsibleContent: PropTypes.func
};

export default TableResponsiveCard;
